/**
 * Orchestration for the Find & Replace widget — subscribes to widget events,
 * calls the pure search/replace modules, performs store mutations + iframe
 * scroll/highlight, and gates the cross-page navigation in
 * document.startViewTransition when available.
 *
 * IMPORTANT: this module does NOT emit `preview:sync-section-html` directly.
 * The centralized history-changed listener in shell.js routes
 * 'Find & Replace'-labeled store mutations through renderPreview ->
 * tryApplySurgicalSync. Single source of truth for preview updates.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { bridge } from '../editing/bridge.js';
import { setActivePage } from '../pages/page-manager.js';
import { search } from '../shared/section-text-search.js';
import { applyReplacementsAcrossState } from '../shared/section-text-replace.js';

const FIND_REPLACE_LABEL = 'Find & Replace';
const REPLACE_ALL_FLASH_STAGGER_MS = 40;

/**
 * Wire the widget to the rest of the app. Returns a teardown function.
 */
export function initFindReplaceActions(widget) {
  const onQuery = ({ query, opts }) => handleQuery(widget, query, opts);
  const onReplacementChanged = ({ replacement }) => updateBindSuggestion(widget, replacement);
  const onNavigate = ({ hit, result }) => handleNavigate(hit, result);
  const onReplaceOne = (payload) => handleReplaceOne(widget, payload);
  const onReplaceAll = (payload) => handleReplaceAll(widget, payload);

  eventBus.on('find-replace:query', onQuery);
  eventBus.on('find-replace:replacement-changed', onReplacementChanged);
  eventBus.on('find-replace:navigate', onNavigate);
  eventBus.on('find-replace:replace-one', onReplaceOne);
  eventBus.on('find-replace:replace-all', onReplaceAll);

  return function teardown() {
    eventBus.off('find-replace:query', onQuery);
    eventBus.off('find-replace:replacement-changed', onReplacementChanged);
    eventBus.off('find-replace:navigate', onNavigate);
    eventBus.off('find-replace:replace-one', onReplaceOne);
    eventBus.off('find-replace:replace-all', onReplaceAll);
  };
}

function handleQuery(widget, query, opts) {
  const state = store.getState();
  const out = search(state, query, opts);
  widget.setResults(out);
  // Reset to first hit after a query change.
  widget.setCurrentIndex(0);
  updateBindSuggestion(widget, widget.getReplacement());
}

/**
 * If `replacement` exactly equals one of the user-visible Client Info text
 * fields, surface a "Also bind to <field>" checkbox. Only text-binding
 * keys qualify (name/tagline/email/phone/phoneFormatted/domain/address*),
 * not URL bindings (social/mapLink) where the literal string is a URL the
 * user wouldn't be typing as raw text content.
 */
function updateBindSuggestion(widget, replacement) {
  const value = (replacement || '').trim();
  if (!value) {
    widget.setBindSuggestion(null);
    return;
  }
  const state = store.getState();
  const ci = state.clientInfo || {};
  const addr = ci.address || {};
  const candidates = [
    { key: 'name', label: 'Business Name', value: ci.name },
    { key: 'tagline', label: 'Tagline', value: ci.tagline },
    { key: 'email', label: 'Email', value: ci.email },
    { key: 'phone', label: 'Phone (digits)', value: ci.phone },
    { key: 'phoneFormatted', label: 'Phone', value: ci.phoneFormatted },
    { key: 'domain', label: 'Domain', value: ci.domain },
    { key: 'addressLineOne', label: 'Address Line 1', value: addr.lineOne },
    { key: 'addressLineTwo', label: 'Address Line 2', value: addr.lineTwo },
    { key: 'addressCity', label: 'City', value: addr.city },
    { key: 'addressCounty', label: 'State / County', value: addr.county },
    { key: 'addressPostcode', label: 'ZIP / Postcode', value: addr.postcode }
  ];
  for (const c of candidates) {
    if (c.value && c.value === value) {
      widget.setBindSuggestion({ key: c.key, label: c.label });
      return;
    }
  }
  widget.setBindSuggestion(null);
}

/**
 * Navigate to a search hit: if on another page, switch active page first,
 * then send scroll + highlight to the iframe. Page-switch is wrapped in
 * `document.startViewTransition` when available — fallback path (no VT)
 * also runs cleanly: switch page, wait for renderer:complete, scroll,
 * pulse the match.
 */
async function handleNavigate(hit, result) {
  const state = store.getState();
  const isShared = !!result.sharedKind;
  const onActivePage = isShared || result.pageId === state.activePage;

  const focusInIframe = () => {
    if (!result.instanceId && !hit.eid) return;
    bridge.send('scroll-to-eid', { instanceId: result.instanceId, eid: hit.eid });
    bridge.send('highlight-text-match', {
      instanceId: result.instanceId,
      eid: hit.eid,
      hitId: hit.hitId,
      beforeCtx: hit.beforeCtx,
      match: hit.match,
      afterCtx: hit.afterCtx
    });
  };

  if (onActivePage) {
    focusInIframe();
    return;
  }

  const run = async () => {
    await setActivePage(result.pageId);
    focusInIframe();
  };

  if (typeof document !== 'undefined' && typeof document.startViewTransition === 'function') {
    try {
      await document.startViewTransition(run).finished;
    } catch {
      /* user cancelled / browser aborted — already settled */
    }
  } else {
    await run();
  }
}

function handleReplaceOne(widget, { hit, result, replacement, opts, bindKey, fingerprint }) {
  if (hit.isInsideBinding) {
    widget.setNotice?.(
      'This text is bound to Client Info. Edit the Client Info panel to change it.'
    );
    return;
  }
  const state = store.getState();
  // Single-hit replace flows through the cross-state machinery for one section
  // — same code path, same revalidation, no special case.
  const oneSection = [
    {
      sectionPath: result.sectionPath,
      instanceId: result.instanceId,
      queryFingerprint: fingerprint,
      hits: [hit]
    }
  ];
  const out = applyReplacementsAcrossState(state, oneSection, widget.getQuery(), replacement, {
    ...opts,
    bindKey
  });
  commitUpdates(out.updates, widget, /* refresh */ true, out);
}

function handleReplaceAll(widget, { results, replacement, opts, bindKey, fingerprint }) {
  if (!Array.isArray(results) || results.length === 0) return;
  const state = store.getState();
  // The widget supplies results; replace will revalidate every hit's signature
  // against current section.html and skip stale ones.
  const { results: writableResults, skipped: boundSkipped } = filterWritableResults(results);
  if (!writableResults.length) {
    widget.setNotice?.(
      'All matches are bound to Client Info. Edit the Client Info panel to change them.'
    );
    return;
  }
  const stamped = writableResults.map((r) => ({
    ...r,
    queryFingerprint: r.queryFingerprint || fingerprint
  }));
  const out = applyReplacementsAcrossState(state, stamped, widget.getQuery(), replacement, {
    ...opts,
    bindKey
  });
  commitUpdates(out.updates, widget, /* refresh */ true, {
    ...out,
    skipped: out.skipped + boundSkipped
  });
}

function commitUpdates(updates, widget, refresh, summary = {}) {
  if (!updates.length) {
    if (summary.skipped) {
      widget.setNotice?.(
        `${summary.skipped} match${summary.skipped === 1 ? '' : 'es'} became stale or read-only. Re-run search to refresh.`
      );
    }
    return;
  }
  store.beginTransaction(FIND_REPLACE_LABEL);
  for (const u of updates) store.update(u.path, u.html, FIND_REPLACE_LABEL);
  store.commitTransaction();
  // Cascade polish: staggered section-flash per affected instance. Pure visual
  // feedback — no state effect, gracefully skipped when the iframe doesn't
  // know `flash-section` or prefers-reduced-motion is set.
  updates.forEach((u, i) => {
    if (!u.instanceId) return;
    setTimeout(
      () => bridge.send('flash-section', { instanceId: u.instanceId }),
      i * REPLACE_ALL_FLASH_STAGGER_MS
    );
  });
  // Re-run the current query against the now-mutated state so the result list
  // updates (hits in already-replaced sections drop off, fingerprints stay
  // aligned with the user's current input).
  if (refresh) {
    const out = search(store.getState(), widget.getQuery(), widget.getOpts());
    widget.setResults(out);
    widget.setCurrentIndex(0);
  }
  if (summary.skipped) {
    widget.setNotice?.(
      `${summary.skipped} match${summary.skipped === 1 ? '' : 'es'} became stale or read-only. Re-run search to refresh.`
    );
  }
}

function filterWritableResults(results) {
  let skipped = 0;
  const writable = [];
  for (const result of results) {
    const hits = (result.hits || []).filter((hit) => {
      if (hit.isInsideBinding) {
        skipped++;
        return false;
      }
      return true;
    });
    if (hits.length) writable.push({ ...result, hits });
  }
  return { results: writable, skipped };
}
